import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerPageLayout from '../customer-page-layout/CustomerPageLayout';
import { useLazyGetNotificationByIdQuery } from '../../../service/notification-service';
import { useTenant } from '../../../tenant/branding/useTenant';
import { BounceLoader } from 'react-spinners';
import NotificationDetail from './components/NotificationDetail';
import { NotificationType, UserNotification } from '../../../model/notifications/notifications';
import styles from './NotificationDetailPage.module.scss';
import LoadedImage from '../../../common/components/loaded-image/LoadedImage';

const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toLowerCase();
    return `${datePart}, ${timePart}`;
};

const getBroadcastImage = (notification: UserNotification): string | undefined => {
    if (notification.type !== NotificationType.Broadcast) return undefined;
    const raw: unknown = notification.notificationContext;
    let ctx: any = raw;
    if (typeof raw === 'string') {
        try {
            ctx = JSON.parse(raw);
        } catch {
            return undefined;
        }
    }
    return ctx?.addImage && ctx?.image ? ctx.image : undefined;
};

const NotificationDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { logo, name } = useTenant();
    const [fetchNotification, { data: notification, isFetching, error }] = useLazyGetNotificationByIdQuery();

    useEffect(() => {
        if (id) fetchNotification({ id });
    }, [id, fetchNotification]);

    return (
        <CustomerPageLayout showBackButton={false} contentContainerClass={styles.pageContent}>
            <div className={styles.container}>
                <div className={styles.listSection}>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageHeaderLeft}>
                            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.6667 5.5L9.16667 11L14.6667 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <h1 className={styles.pageTitle}>Notification</h1>
                        </div>
                        <LoadedImage src={logo} alt={name} className={styles.logoIcon} />
                    </div>

                    {isFetching && (
                        <div className={styles.loadingOverlay}>
                            <BounceLoader color="var(--customer-primary)" />
                        </div>
                    )}

                    <div className={styles.card}>
                        {error ? (
                            <div className={styles.error}>Failed to load notification.</div>
                        ) : notification ? (
                            <>
                                {getBroadcastImage(notification) && (
                                    <img
                                        className={styles.broadcastImage}
                                        src={getBroadcastImage(notification)}
                                        alt={notification.title}
                                    />
                                )}
                                {notification.type !== NotificationType.BpsBilling && (
                                    <div className={styles.cardHeader}>
                                        <div className={styles.titleRow}>
                                            <h2 className={styles.title}>{notification.title}</h2>
                                            {!notification.readAt && <span className={styles.unreadDot} />}
                                        </div>
                                        <span className={styles.date}>{formatDateTime(notification.createdAt)}</span>
                                    </div>
                                )}
                                <NotificationDetail
                                    notification={notification}
                                    isLoading={false}
                                    onClose={() => {}}
                                    hideCloseButton
                                />
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </CustomerPageLayout>
    );
};

export default NotificationDetailPage;
